import React, { useState, useCallback } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import SwapHorizontalCircleIcon from '@material-ui/icons/SwapHorizontalCircle';
import SwapVerticalCircleIcon from '@material-ui/icons/SwapVerticalCircle';
import { FrameData, getClassColor } from './SessionVisualize';
import { Coordinate } from '../VideoPlayer/VideoTypes';
import { TSession } from './Sessions';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {
    backgroundColor: '#ffffff',
    marginRight: '6px',
  },
  icons: {
    display: 'flex',
    margin: '4px',
  },
  activeIcon: {
    fontSize: '24px',
    color: '#fff',
    backgroundColor: '#ff5a0f',
    borderRadius: '12px',
  },
  icon: {
    color: '#949494',
  },
}));

const fieldSizeDefaults = { width: 105, height: 68 };
const svgObjectSize = { width: 4, height: 4 };
const svgFieldOffset: number = 6;

type Props = {
  session: TSession | undefined;
  currentFrame: FrameData | undefined;
};

const Radar = (props: Props) => {
  const classes = useStyles();

  const { session, currentFrame } = props;

  const [flipX, setFlipX] = useState<boolean>(false);
  const [flipY, setFlipY] = useState<boolean>(false);

  const fieldWidth: number = session?.pitch?.fieldLength
    ? session.pitch.fieldLength * 0.001
    : fieldSizeDefaults.width;
  const fieldHeight: number = session?.pitch?.fieldWidth
    ? session.pitch.fieldWidth * 0.001
    : fieldSizeDefaults.height;

  // For now we draw inside of the field when width or height differs 10% off the fifa standard measurement.
  const drawInsideField: boolean = fieldWidth >= 100 && fieldWidth <= 120 && fieldHeight >= 64 && fieldHeight <= 75;

  const transformPosition = useCallback(
    (x: number, y: number, flipX: boolean, flipY: boolean) => {
      //swap x and y positions, we treat y horizontally and x vertically in radar view.
      let t = x;
      x = y;
      y = t;

      //handle flipping of the field
      x = flipX ? x : fieldWidth - x;
      y = flipY ? y : fieldHeight - y;

      //return position object
      return { x, y };
    },
    [fieldHeight, fieldWidth]
  );

  let i: number = 0;
  const objectSvgs = currentFrame?.objects.map((obj) => {
    const coord: Coordinate = transformPosition(obj.position.x, obj.position.y, flipX, flipY);
    const classColor: string = session ? getClassColor(session, obj.type) : '#ff0';
    const shirtNumber: number | undefined = obj.lineupEntry?.shirtNumber;
    return (
      <svg key={i++}>
        <rect
          width={svgObjectSize.width}
          height={svgObjectSize.height}
          rx={svgObjectSize.width * 0.5}
          x={coord.x - svgObjectSize.width * 0.5}
          y={coord.y - svgObjectSize.height * 0.5}
          fill="#fff"
          opacity={1}
          style={{
            stroke: classColor === '#ffffff' ? '#444' : classColor,
            strokeWidth: 1.5,
          }}
        />
        {shirtNumber && (
          <text
            x={coord.x}
            y={coord.y + 6}
            textLength={5}
            fontSize={4}
            fontWeight={100}
            textAnchor="middle"
            stroke="#000"
            strokeWidth={0.2}>
            {shirtNumber}
          </text>
        )}
      </svg>
    );
  });

  return (
    <div className={classes.root}>
      <div className={classes.icons}>
        <SwapVerticalCircleIcon
          className={flipY ? classes.activeIcon : classes.icon}
          onClick={() => setFlipY((prevFlipY) => !prevFlipY)}
        />
        <br />
        <SwapHorizontalCircleIcon
          className={flipX ? classes.activeIcon : classes.icon}
          onClick={() => setFlipX((prevFlipX) => !prevFlipX)}
        />
      </div>

      <svg
        width="100%"
        height="100%"
        viewBox={
          -svgFieldOffset * 0.5 +
          ' ' +
          -svgFieldOffset * 0.5 +
          ' ' +
          (fieldWidth + svgFieldOffset).toString() +
          ' ' +
          (fieldHeight + svgFieldOffset).toString()
        }
        fill="#ffffff"
        xmlns="http://www.w3.org/2000/svg"
        stroke="#888"
        strokeWidth={0.5}>
        {/*Side- & half-way lines*/}
        <rect x={0} y={0} width={fieldWidth} height={fieldHeight} />
        <path d={'M ' + fieldWidth * 0.5 + ' 0 ' + fieldWidth * 0.5 + ' ' + fieldHeight} />

        {/*Centre Spot*/}
        <circle cx={fieldWidth * 0.5} cy={fieldHeight * 0.5} r={0.5} fill="none" />

        {/*Only draw this when required*/}
        {drawInsideField && (
          <>
            {/*Centre Circle*/}
            <circle cx={fieldWidth * 0.5} cy={fieldHeight * 0.5} r={9.15} fill="none" />

            {/*Corners*/}
            <path d="M 0 2 A2 2, 0, 0, 0, 2 0" />
            <path d={'M ' + (fieldWidth - 2).toString() + ' 0 A-2 2, 0, 0, 0, ' + fieldWidth + ' 2'} />
            <path d={'M 0 ' + (fieldHeight - 2).toString() + ' A2 -2, 0, 0, 1, 2 ' + fieldHeight} />
            <path
              d={
                'M ' +
                (fieldWidth - 2).toString() +
                ' ' +
                fieldHeight +
                ' A2 -2, 0, 0, 1, ' +
                fieldWidth +
                ' ' +
                (fieldHeight - 2).toString()
              }
            />

            {/*Behind Goal L&R*/}
            <rect x={-2.4} y={fieldHeight * 0.5 - 3.65} width={2.4} height={7.3} fill="none" />
            <rect x={fieldWidth} y={fieldHeight * 0.5 - 3.65} width={2.4} height={7.3} fill="none" />

            {/*Penalty Area L&R*/}
            <rect x={0} y={fieldHeight * 0.5 - 20.15} width={16.5} height={40.3} fill="none" />
            <rect x={fieldWidth - 16.5} y={fieldHeight * 0.5 - 20.15} width={16.5} height={40.3} fill="none" />

            {/*6-Yard Box L&R*/}
            <rect x={0} y={fieldHeight * 0.5 - 9.15} width={5.5} height={18.3} fill="none" />
            <rect x={fieldWidth - 5.5} y={fieldHeight * 0.5 - 9.15} width={5.5} height={18.3} fill="none" />

            {/*Penalty Spot L&R*/}
            <circle cx={11} cy={fieldHeight * 0.5} r={0.3} fill="none" />
            <circle cx={fieldWidth - 11} cy={fieldHeight * 0.5} r={0.3} fill="none" />

            {/*Penalty Radius L&R*/}
            <path
              d={
                'M 16.5 ' +
                (fieldHeight * 0.5 + 7.5).toString() +
                ' A9.15 9.15, 0, 0, 0, 16.5 ' +
                (fieldHeight * 0.5 - 7.5).toString()
              }
            />
            <path
              d={
                'M ' +
                (fieldWidth - 16.5).toString() +
                ' ' +
                (fieldHeight * 0.5 - 7.5).toString() +
                ' A9.15 9.15, 0, 0, 0, ' +
                (fieldWidth - 16.5).toString() +
                ' ' +
                (fieldHeight * 0.5 + 7.5).toString()
              }
            />
          </>
        )}

        {/*Moving objects*/}
        {objectSvgs}
      </svg>
    </div>
  );
};

export default Radar;
